import _toConsumableArray from "/usr/app/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import _objectSpread from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectSpread2.js";
import _regenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/regenerator.js";
import _asyncToGenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
var __jsx = React.createElement;
import React, { createContext, useContext, useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef, useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";
import useCookieClient from "@hooks/useCookieClient";
import { updateCart, checkoutCart, getCart } from "@/apis/oils/cartApi";
import FullPageContext from "@/common/Contexts/FullPageContext";
var CartContext = /*#__PURE__*/createContext(undefined);

// Per-item count store, external to React state/context. `items` changing
// (any add/remove/quantity update) would otherwise re-render every consumer
// of useCart() — including every product card on a listing page — since they
// all read the same context value. Cards instead subscribe here via
// useItemCount(id), so a change to one item's count only notifies that item's
// own listeners, not the whole page.
var createItemCountStore = function createItemCountStore() {
  var counts = new Map();
  var listeners = new Map();
  var getCount = function getCount(id) {
    var _counts$get;
    return (_counts$get = counts.get(id)) !== null && _counts$get !== void 0 ? _counts$get : 0;
  };
  var subscribe = function subscribe(id, listener) {
    var set = listeners.get(id);
    if (!set) {
      set = new Set();
      listeners.set(id, set);
    }
    set.add(listener);
    return function () {
      set["delete"](listener);
      if (set.size === 0) listeners["delete"](id);
    };
  };
  var sync = function sync(items) {
    var nextIds = new Set(items.map(function (i) {
      return i.id;
    }));
    var changed = new Set();
    items.forEach(function (item) {
      if (counts.get(item.id) !== item.count) changed.add(item.id);
    });
    counts.forEach(function (_, id) {
      if (!nextIds.has(id)) changed.add(id);
    });
    counts.clear();
    items.forEach(function (item) {
      return counts.set(item.id, item.count);
    });
    changed.forEach(function (id) {
      var _listeners$get;
      (_listeners$get = listeners.get(id)) === null || _listeners$get === void 0 ? void 0 : _listeners$get.forEach(function (listener) {
        return listener();
      });
    });
  };
  return {
    getCount: getCount,
    subscribe: subscribe,
    sync: sync
  };
};
var CART_ID_KEY = "cart_id";
var toApiItems = function toApiItems(items) {
  return items.map(function (i) {
    return {
      type: i.type,
      id: i.id,
      count: i.count
    };
  });
};

// Rebuild local ICartItem[] from getCart response (for badge counts after migration)
var itemsFromCartData = function itemsFromCartData(data) {
  var block = data.find(function (b) {
    return b.component_type === "cart_items";
  });
  if (!block) return [];
  return block.content.filter(function (b) {
    return b.component_type === "cart_item_oil";
  }).map(function (b) {
    return {
      id: b.content.id,
      type: "coupon",
      count: b.content.count,
      price: b.content.price
    };
  });
};

// Pull the cart blocks out of an SSR full-page payload (the oil pages embed
// cart_items / cart_summary / cart_cta blocks alongside their content).
var ssrCartBlocks = function ssrCartBlocks(data) {
  if (!Array.isArray(data)) return null;
  var blocks = data.filter(function (b) {
    return b.component_type === "cart_items" || b.component_type === "cart_summary" || b.component_type === "cart_cta";
  });
  return blocks.length ? blocks : null;
};
var cartIdFromBlocks = function cartIdFromBlocks(blocks) {
  var _header$cart_id;
  var itemsBlock = blocks === null || blocks === void 0 ? void 0 : blocks.find(function (b) {
    return b.component_type === "cart_items";
  });
  var header = itemsBlock === null || itemsBlock === void 0 ? void 0 : itemsBlock.content.find(function (b) {
    return b.component_type === "cart_header";
  });
  return (_header$cart_id = header === null || header === void 0 ? void 0 : header.cart_id) !== null && _header$cart_id !== void 0 ? _header$cart_id : null;
};
var ItemCountStoreContext = /*#__PURE__*/createContext(undefined);
// Stable action dispatchers, isolated from CartContext's volatile value so
// components that only need to add/remove/update (e.g. product cards) don't
// re-render when unrelated cart state (totals, isSyncing, cartData) changes.
var CartActionsContext = /*#__PURE__*/createContext(undefined);
export var useCartActions = function useCartActions() {
  var ctx = useContext(CartActionsContext);
  if (!ctx) throw new Error("useCartActions must be used within CartProvider");
  return ctx;
};

// Subscribes only to a single item's count, so the many product cards on a
// listing page don't re-render when a *different* item's quantity changes.
export var useItemCount = function useItemCount(id) {
  var store = useContext(ItemCountStoreContext);
  if (!store) throw new Error("useItemCount must be used within CartProvider");
  return useSyncExternalStore(function (listener) {
    return store.subscribe(id, listener);
  }, function () {
    return store.getCount(id);
  }, function () {
    return store.getCount(id);
  } // getServerSnapshot: store is seeded synchronously from SSR items above
  );
};
export var CartProvider = function CartProvider(_ref) {
  var _ctaBlock$content$pri, _ctaBlock$content, _ctaBlock$content$pri2;
  var children = _ref.children;
  // Feed the context from the SSR full-page payload so the cart badge is correct
  // on the very first paint (landing/refresh) instead of waiting for a client
  // effect to seed it. The server reads it from FullPageContext (which wraps
  // <App>); the client reads the same payload from the injected global (there is
  // no FullPageContext.Provider on the client). Same data both sides => the
  // first render matches and there is no hydration mismatch.
  var fullPageDataFromContext = useContext(FullPageContext);
  var ssrFullPageData = __IS_CSR__ ? typeof window !== "undefined" ? window.FULL_PAGE_DATA : null : fullPageDataFromContext;
  var ssrBlocks = ssrCartBlocks(ssrFullPageData);
  var _useState = useState(function () {
      return ssrBlocks ? itemsFromCartData(ssrBlocks) : [];
    }),
    items = _useState[0],
    setItems = _useState[1];
  // Seeded synchronously (not in an effect) so it already reflects `items` on
  // the very first render — effects never run on the server, and
  // useSyncExternalStore's getServerSnapshot needs a correct value from the
  // start, not just after the client mounts.
  var itemCountStore = useRef();
  if (!itemCountStore.current) {
    itemCountStore.current = createItemCountStore();
    itemCountStore.current.sync(items);
  }
  // Layout effect, not a plain effect: it must run — and notify useItemCount
  // subscribers — before the browser paints. A plain effect fires after
  // paint, so the card would briefly paint with the *previous* count, then
  // flash to the new one once the effect caught up.
  useLayoutEffect(function () {
    itemCountStore.current.sync(items);
  }, [items]);
  var _useState2 = useState(function () {
      return cartIdFromBlocks(ssrBlocks);
    }),
    cartId = _useState2[0],
    setCartId = _useState2[1];
  var _useState3 = useState(function () {
      return ssrBlocks;
    }),
    cartData = _useState3[0],
    setCartData = _useState3[1];
  var _useState4 = useState(false),
    isLoading = _useState4[0],
    setIsLoading = _useState4[1];
  // True from the instant a +/- click schedules a sync until the server
  // confirms it. The "Add More" button is disabled while this is true so a hard
  // navigation can't out-race the debounced write and land on a stale listing.
  var _useState5 = useState(false),
    isSyncing = _useState5[0],
    setIsSyncing = _useState5[1];
  var _useState6 = useState(0),
    cartCountFromMeta = _useState6[0],
    setCartCountFromMeta = _useState6[1];
  // Meta count is a placeholder until real items arrive; once they do, an empty
  // `items` means an empty cart, not "not loaded yet".
  var _useState7 = useState(!!ssrBlocks),
    hasLiveItems = _useState7[0],
    setHasLiveItems = _useState7[1];
  var applyItems = useCallback(function (next) {
    setHasLiveItems(true);
    setItems(next);
  }, []);

  // Debounced server sync + out-of-order response guard.
  // Rapid +/- clicks update `items` optimistically and collapse into a single
  // server call; only the latest response is allowed to reconcile state, so a
  // stale response can't snap the quantity back to an old value.
  var syncTimer = useRef(null);
  var syncSeq = useRef(0);
  var pendingItemsRef = useRef(null);
  var inFlightRef = useRef(false); // true while an update-cart request is in flight; queues, never overlaps
  var cartIdRef = useRef(null);
  var SYNC_DELAY_MS = 350;
  useEffect(function () {
    cartIdRef.current = cartId;
  }, [cartId]);
  useEffect(function () {
    if (typeof window === "undefined") return;
    var match = document.cookie.split(";").map(function (c) {
      return c.trim();
    }).find(function (c) {
      return c.startsWith("".concat(CART_ID_KEY, "="));
    });
    var cookieId = match ? decodeURIComponent(match.slice(CART_ID_KEY.length + 1)) : null;
    if (cookieId) {
      setCartId(cookieId);
      cartIdRef.current = cookieId;
    }
  }, []);
  var persistLocal = useCallback(function (nextCartId) {
    if (typeof window === "undefined") return;
    if (nextCartId) {
      document.cookie = "".concat(CART_ID_KEY, "=").concat(nextCartId, "; path=/; max-age=2592000; SameSite=Lax");
    } else {
      document.cookie = "".concat(CART_ID_KEY, "=; path=/; max-age=0");
    }
  }, []);

  // Fire the actual network request. Never runs two of these concurrently —
  // see syncToServer's queueing below — so it's always safe to apply the
  // response directly.
  var flushSync = useCallback(/*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee(nextItems) {
      var _header$cart_id2;
      var seq, currentCartId, data, queued, itemsBlock, header, reconciledId;
      return _regenerator().w(function (_context) {
        while (1) switch (_context.n) {
          case 0:
            seq = ++syncSeq.current;
            currentCartId = cartIdRef.current;
            inFlightRef.current = true;
            setIsLoading(true);
            _context.n = 1;
            return updateCart(currentCartId, toApiItems(nextItems));
          case 1:
            data = _context.v;
            inFlightRef.current = false;
            if (!(seq !== syncSeq.current)) {
              _context.n = 2;
              break;
            }
            return _context.a(2);
          case 2:
            if (data) {
              _context.n = 3;
              break;
            }
            setIsLoading(false);
            setIsSyncing(false);
            return _context.a(2);
          case 3:
            // A newer edit may have queued up while this request was in flight
            // (pendingItemsRef got overwritten instead of firing, since a sync was
            // already running). That queued snapshot is already ahead of what
            // this response describes — applying `reconciledItems` now would
            // visibly snap the UI back before the queued flush corrects it again
            // a moment later. Skip reconciling `items` in that case; the queued
            // flush's own response is what should reconcile local state.
            queued = pendingItemsRef.current;
            pendingItemsRef.current = null;

            // Extract cart_id from the cart_header block inside cart_items
            itemsBlock = data.find(function (b) {
              return b.component_type === "cart_items";
            });
            header = itemsBlock === null || itemsBlock === void 0 ? void 0 : itemsBlock.content.find(function (b) {
              return b.component_type === "cart_header";
            });
            reconciledId = (_header$cart_id2 = header === null || header === void 0 ? void 0 : header.cart_id) !== null && _header$cart_id2 !== void 0 ? _header$cart_id2 : currentCartId;
            if (reconciledId) {
              setCartId(reconciledId);
              cartIdRef.current = reconciledId;
            }
            if (!queued) applyItems(itemsFromCartData(data));
            setCartData(data);
            persistLocal(reconciledId);
            setIsLoading(false);
            if (queued) {
              flushSync(queued);
            } else {
              setIsSyncing(false);
            }
          case 4:
            return _context.a(2);
        }
      }, _callee);
    }));
    return function (_x) {
      return _ref2.apply(this, arguments);
    };
  }(), [persistLocal]);

  // Debounce rapid +/- clicks into a single server call, and queue behind
  // any request already in flight — update-cart calls run strictly one at a
  // time, in order, so the server never has to reconcile two overlapping
  // writes for the same cart.
  var syncToServer = useCallback(function (nextItems) {
    setIsSyncing(true);
    pendingItemsRef.current = nextItems;
    if (syncTimer.current) clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(function () {
      syncTimer.current = null;
      if (inFlightRef.current) return; // flushSync's own continuation will pick up pendingItemsRef
      var items = pendingItemsRef.current;
      pendingItemsRef.current = null;
      if (items) flushSync(items);
    }, SYNC_DELAY_MS);
  }, [flushSync]);

  // Flush any pending sync if the page is being unloaded (e.g. navigating to
  // the cart) so the server isn't left with a stale snapshot.
  useEffect(function () {
    if (typeof window === "undefined") return;
    var flushNow = function flushNow() {
      if (syncTimer.current) {
        clearTimeout(syncTimer.current);
        syncTimer.current = null;
      }
      if (inFlightRef.current) return; // let the in-flight request's own continuation flush it
      var items = pendingItemsRef.current;
      pendingItemsRef.current = null;
      if (items) flushSync(items);
    };
    window.addEventListener("pagehide", flushNow);
    return function () {
      return window.removeEventListener("pagehide", flushNow);
    };
  }, [flushSync]);
  var loadCart = useCallback(/*#__PURE__*/_asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee2() {
    var _document$cookie$spli, _document$cookie$spli2;
    var cookieId, id, seq, data;
    return _regenerator().w(function (_context2) {
      while (1) switch (_context2.p = _context2.n) {
        case 0:
          cookieId = typeof window !== "undefined" ? (_document$cookie$spli = (_document$cookie$spli2 = document.cookie.split(";").map(function (c) {
            return c.trim();
          }).find(function (c) {
            return c.startsWith("".concat(CART_ID_KEY, "="));
          })) === null || _document$cookie$spli2 === void 0 ? void 0 : _document$cookie$spli2.slice(CART_ID_KEY.length + 1)) !== null && _document$cookie$spli !== void 0 ? _document$cookie$spli : null : null;
          id = cookieId || cartId;
          seq = syncSeq.current; // bail if a +/- click schedules a newer write while this GET is in flight
          setIsLoading(true);
          _context2.p = 1;
          _context2.n = 2;
          return getCart(id !== null && id !== void 0 ? id : null);
        case 2:
          data = _context2.v;
          if (data) {
            setCartData(data);
            // Live cart is authoritative: replace local items (even when empty) so
            // the badge and per-option counts reflect the real server state —
            // but only if nothing more recent is queued, else this stale GET
            // would drop an item the user just added.
            if (seq === syncSeq.current) applyItems(itemsFromCartData(data));
          } else {
            setCartData([]);
          }
        case 3:
          _context2.p = 3;
          setIsLoading(false);
          return _context2.f(3);
        case 4:
          return _context2.a(2);
      }
    }, _callee2, null, [[1,, 3, 4]]);
  })), [cartId]);

  // Ref, not a dep: fires on pathname only, not when cartId settles.
  var _useLocation = useLocation(),
    pathname = _useLocation.pathname;
  var _useCookieClient = useCookieClient(),
    getCookieClient = _useCookieClient.getCookieClient;
  var loadCartRef = useRef(loadCart);
  loadCartRef.current = loadCart;
  var didSkipInitialLoad = useRef(false);
  useEffect(function () {
    if (!didSkipInitialLoad.current) {
      didSkipInitialLoad.current = true;
      if (ssrBlocks || !getCookieClient(CART_ID_KEY)) return;
    }
    loadCartRef.current();
  }, [pathname]);
  var addItem = useCallback(function (item) {
    applyItems(function (prev) {
      var existing = prev.find(function (i) {
        return i.id === item.id;
      });
      var next = existing ? prev.map(function (i) {
        return i.id === item.id ? _objectSpread(_objectSpread({}, i), {}, {
          count: i.count + 1
        }) : i;
      }) : [].concat(_toConsumableArray(prev), [_objectSpread(_objectSpread({}, item), {}, {
        count: 1
      })]);
      syncToServer(next);
      return next;
    });
  }, [applyItems, syncToServer]);
  var removeItem = useCallback(function (id) {
    applyItems(function (prev) {
      var next = prev.filter(function (i) {
        return i.id !== id;
      });
      syncToServer(next);
      return next;
    });
  }, [applyItems, syncToServer]);
  var updateQuantity = useCallback(function (id, count) {
    if (count <= 0) {
      removeItem(id);
      return;
    }
    applyItems(function (prev) {
      var next = prev.map(function (i) {
        return i.id === id ? _objectSpread(_objectSpread({}, i), {}, {
          count: count
        }) : i;
      });
      syncToServer(next);
      return next;
    });
  }, [applyItems, removeItem, syncToServer]);
  var clearCart = useCallback(function () {
    if (syncTimer.current) {
      clearTimeout(syncTimer.current);
      syncTimer.current = null;
    }
    pendingItemsRef.current = null;
    syncSeq.current++; // invalidate any in-flight sync response
    var prevCartId = cartIdRef.current;
    applyItems([]);
    setCartId(null);
    cartIdRef.current = null;
    setCartData([]);
    persistLocal(null);
    updateCart(prevCartId, []).then(function (d) {
      if (d) setCartData(d);
    });
  }, [applyItems, persistLocal]);
  var getItemCount = useCallback(function (id) {
    var _items$find$count, _items$find;
    return (_items$find$count = (_items$find = items.find(function (i) {
      return i.id === id;
    })) === null || _items$find === void 0 ? void 0 : _items$find.count) !== null && _items$find$count !== void 0 ? _items$find$count : 0;
  }, [items]);
  var seedCartMeta = useCallback(function (newCartId, count) {
    if (newCartId) {
      setCartId(newCartId);
      cartIdRef.current = newCartId;
      persistLocal(newCartId);
    }
    setCartCountFromMeta(count);
  }, [persistLocal]);
  var seedCart = useCallback(function (blocks) {
    var _header$cart_id3;
    var itemsBlock = blocks.find(function (b) {
      return b.component_type === "cart_items";
    });
    var header = itemsBlock === null || itemsBlock === void 0 ? void 0 : itemsBlock.content.find(function (b) {
      return b.component_type === "cart_header";
    });
    var newCartId = (_header$cart_id3 = header === null || header === void 0 ? void 0 : header.cart_id) !== null && _header$cart_id3 !== void 0 ? _header$cart_id3 : null;
    setCartData(blocks);
    var synced = itemsFromCartData(blocks);
    if (synced.length > 0) applyItems(synced);
    if (newCartId) {
      setCartId(newCartId);
      cartIdRef.current = newCartId;
      persistLocal(newCartId);
    }
  }, [persistLocal]);
  var checkout = useCallback(/*#__PURE__*/function () {
    var _ref4 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee3(successUrl, failureUrl) {
      var _document$cookie$spli3, _document$cookie$spli4;
      var cookieId, id;
      return _regenerator().w(function (_context3) {
        while (1) switch (_context3.p = _context3.n) {
          case 0:
            cookieId = typeof window !== "undefined" ? (_document$cookie$spli3 = (_document$cookie$spli4 = document.cookie.split(";").map(function (c) {
              return c.trim();
            }).find(function (c) {
              return c.startsWith("".concat(CART_ID_KEY, "="));
            })) === null || _document$cookie$spli4 === void 0 ? void 0 : _document$cookie$spli4.slice(CART_ID_KEY.length + 1)) !== null && _document$cookie$spli3 !== void 0 ? _document$cookie$spli3 : null : null;
            id = cookieId || cartId;
            setIsLoading(true);
            _context3.p = 1;
            _context3.n = 2;
            return checkoutCart(id, successUrl, failureUrl);
          case 2:
            return _context3.a(2, _context3.v);
          case 3:
            _context3.p = 3;
            setIsLoading(false);
            return _context3.f(3);
          case 4:
            return _context3.a(2);
        }
      }, _callee3, null, [[1,, 3, 4]]);
    }));
    return function (_x2, _x3) {
      return _ref4.apply(this, arguments);
    };
  }(), [cartId]);
  var totalItems = hasLiveItems || items.length > 0 ? items.reduce(function (sum, i) {
    return sum + i.count;
  }, 0) : cartCountFromMeta;
  var totalCurrentPrice = items.reduce(function (sum, i) {
    return sum + i.price.current * i.count;
  }, 0);
  var totalOriginalPrice = items.reduce(function (sum, i) {
    return sum + i.price.original * i.count;
  }, 0);
  var ctaBlock = cartData === null || cartData === void 0 ? void 0 : cartData.find(function (b) {
    return b.component_type === "cart_cta";
  });
  var totalFinalPrice = (_ctaBlock$content$pri = ctaBlock === null || ctaBlock === void 0 ? void 0 : (_ctaBlock$content = ctaBlock.content) === null || _ctaBlock$content === void 0 ? void 0 : (_ctaBlock$content$pri2 = _ctaBlock$content.price) === null || _ctaBlock$content$pri2 === void 0 ? void 0 : _ctaBlock$content$pri2.current) !== null && _ctaBlock$content$pri !== void 0 ? _ctaBlock$content$pri : totalCurrentPrice;
  var actions = useMemo(function () {
    return {
      addItem: addItem,
      removeItem: removeItem,
      updateQuantity: updateQuantity
    };
  }, [addItem, removeItem, updateQuantity]);
  var value = useMemo(function () {
    return {
      items: items,
      cartId: cartId,
      cartData: cartData,
      totalItems: totalItems,
      totalCurrentPrice: totalCurrentPrice,
      totalOriginalPrice: totalOriginalPrice,
      isLoading: isLoading,
      isSyncing: isSyncing,
      totalFinalPrice: totalFinalPrice,
      addItem: addItem,
      removeItem: removeItem,
      updateQuantity: updateQuantity,
      clearCart: clearCart,
      getItemCount: getItemCount,
      loadCart: loadCart,
      seedCart: seedCart,
      seedCartMeta: seedCartMeta,
      checkout: checkout
    };
  }, [items, cartId, cartData, totalItems, totalCurrentPrice, totalOriginalPrice, isLoading, isSyncing, totalFinalPrice, addItem, removeItem, updateQuantity, clearCart, getItemCount, loadCart, seedCart, seedCartMeta, checkout]);
  return /*#__PURE__*/React.createElement(ItemCountStoreContext.Provider, {
    value: itemCountStore.current
  }, /*#__PURE__*/React.createElement(CartActionsContext.Provider, {
    value: actions
  }, /*#__PURE__*/React.createElement(CartContext.Provider, {
    value: value
  }, children)));
};
export var useCart = function useCart() {
  var ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};